import { Component, input, output, signal, computed, inject, linkedSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, RouterLink, Router } from '@angular/router';
import { NgClass, NgTemplateOutlet, SlicePipe } from '@angular/common';
import { LucideAngularModule, ChevronDown, ChevronRight, X, LogOut } from 'lucide-angular';
import { filter, map, startWith } from 'rxjs';

import { ThemeService } from '../../services/theme.service';
import { AuthService } from '../../auth/auth.service';
import { NavigationService } from '../../services/navigation.service';
import { MeService } from '../../services/me.service';
import { TenantService } from '../../services/tenant.service';
import { type NavItem } from '../navigation.config';
import { TenantSelectorComponent } from '../../../shared/components/tenant-selector/tenant-selector.component';
import { TenantLogoComponent } from '../../../shared/components/tenant-logo/tenant-logo.component';

@Component({
  selector: 'app-sidebar',
  imports: [RouterLink, NgClass, NgTemplateOutlet, SlicePipe, LucideAngularModule, TenantSelectorComponent, TenantLogoComponent],
  templateUrl: './app-sidebar.component.html',
  styleUrl: './app-sidebar.component.css'
})
export class AppSidebarComponent {
  mobile = input(false);
  open = input(false);
  collapsed = input(false);
  isModalOpen = input(false);
  onClose = output<void>();
  onToggleCollapsed = output<void>();
  onLogout = output<void>();

  readonly themeService = inject(ThemeService);
  readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly navigationService = inject(NavigationService);
  private readonly meService = inject(MeService);
  private readonly tenantService = inject(TenantService);

  /** Top-level menu keys reserved for the development user (bootstrap admin). */
  private static readonly DEV_ONLY_KEYS = new Set(['centro-control']);
  private static readonly ROUTE_ALIASES = new Map<string, string[]>([
    ['/reconciliation/standard', ['/reconciliation/fiscal', '/reconciliation/legacy/standard']],
    ['/reconciliation/legacy/standard', ['/reconciliation/fiscal', '/reconciliation/standard']],
    ['/reconciliation/fiscal', ['/reconciliation/standard', '/reconciliation/legacy/standard']],
    ['/reconciliation/advanced', ['/reconciliation/fiscal-tesoreria', '/reconciliation/legacy/advanced']],
    ['/reconciliation/legacy/advanced', ['/reconciliation/fiscal-tesoreria', '/reconciliation/advanced']],
    ['/reconciliation/fiscal-tesoreria', ['/reconciliation/advanced', '/reconciliation/legacy/advanced']],
    // Pantallas de Multifuente que no viven bajo /source-agnostic/plans (detalle de ejecución, integral, vista de operación…).
    ['/reconciliation/source-agnostic/plans', [
      '/reconciliation/source-agnostic',
      '/reconciliation/executions',
      '/reconciliation/general',
      '/reconciliation/sedes',
      '/reconciliation/novedades',
      '/reconciliation/correcciones',
      '/reconciliation/hoy',
      '/reconciliation/pendientes',
      '/reconciliation/cuadre-ventas',
      '/reconciliation/puesta-en-marcha'
    ]]
  ]);

  /** Configuración y Cuadre del mes son compartidas: `?from=` dice desde qué conciliación se abrieron. */
  private static readonly CONTEXT_ROUTES: Readonly<Record<string, string>> = {
    fiscal: '/reconciliation/fiscal',
    treasury: '/reconciliation/fiscal-tesoreria',
    multisource: '/reconciliation/source-agnostic/plans'
  };

  /**
   * Backend-driven navigation tree (already filtered by tenant features + user permissions).
   * As frontend defense-in-depth, hides the dev-only control center for non-bootstrap users.
   */
  get navigation(): NavItem[] {
    const items = this.navigationService.navigation();
    return this.meService.isBootstrapAdmin()
      ? items
      : items.filter(item => !item.key || !AppSidebarComponent.DEV_ONLY_KEYS.has(item.key));
  }
  readonly ChevronDown = ChevronDown;
  readonly ChevronRight = ChevronRight;
  readonly X = X;
  readonly LogOut = LogOut;

  expandedMenus = signal<string[]>([]);
  manuallyCollapsedMenus = signal<string[]>([]);

  isDesktopCollapsed = computed(() => !this.mobile() && this.collapsed());

  readonly currentTenant = computed(() => {
    const key = this.tenantService.tenantKey();
    if (!key) return null;
    return this.meService.me()?.tenants.find(t => t.tenantKey === key) || null;
  });

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
      startWith(this.router.url)
    ),
    { requireSync: true }
  );

  /**
   * Ítem del menú marcado. Si la página actual no corresponde a ningún ítem pero sigue en la misma sección
   * (p. ej. /reconciliation/…), se conserva el último marcado para que el submenú no se "desmarque".
   */
  readonly activeMenuItem = linkedSignal<{ url: string; items: NavItem[] }, NavItem | null>({
    source: () => ({ url: this.currentUrl(), items: this.flattenNavigation(this.navigation) }),
    computation: ({ url, items }, previous) =>
      this.matchMenuItem(url, items) ?? this.keepPreviousInSection(url, previous?.value ?? null, items)
  });

  isActiveItem(item: NavItem): boolean {
    return this.activeMenuItem() === item;
  }

  isExpanded(name: string): boolean {
    return this.expandedMenus().includes(name);
  }

  isMenuOpen(item: NavItem): boolean {
    if (this.manuallyCollapsedMenus().includes(item.name)) {
      return false;
    }

    return this.isExpanded(item.name) || this.hasActiveChild(item);
  }

  toggleMenu(item: NavItem) {
    const name = item.name;

    if (this.isMenuOpen(item)) {
      this.expandedMenus.update(prev => prev.filter(menu => menu !== name));
      this.manuallyCollapsedMenus.update(prev =>
        prev.includes(name) ? prev : [...prev, name]
      );
      return;
    }

    this.manuallyCollapsedMenus.update(prev => prev.filter(menu => menu !== name));
    this.expandedMenus.update(prev =>
      prev.includes(name) ? prev : [...prev, name]
    );
  }

  handleParentClick(item: NavItem) {
    if (this.isDesktopCollapsed()) {
      this.onToggleCollapsed.emit();
    }
    this.toggleMenu(item);
  }

  navigateTo(item: NavItem) {
    if (!item.href) {
      return;
    }

    void this.router.navigateByUrl(item.href);

    if (this.mobile()) {
      this.onClose.emit();
    }
  }

  closeAfterExternalNavigation(): void {
    if (this.mobile()) {
      this.onClose.emit();
    }
  }

  hasChildren(item: NavItem): boolean {
    return !!(item.children && item.children.length > 0);
  }

  hasActiveChild(item: NavItem): boolean {
    return !!item.children?.some(child => this.isActiveItem(child));
  }

  get activeItemClass() {
    return 'app-sidebar-item-active';
  }

  get inactiveItemClass() {
    return 'text-[var(--sidebar-bg-foreground,#000)] hover:bg-[color-mix(in_oklch,var(--sidebar-bg-foreground,#000)_6%,transparent)] dark:text-[var(--text-primary)] dark:hover:bg-[var(--button-ghost-hover)]';
  }

  get expandedItemClass() {
    return 'h-[52px] rounded-lg px-6 text-sm font-normal leading-6';
  }

  get collapsedItemClass() {
    return 'mx-auto size-[52px] justify-center rounded-lg px-0';
  }

  itemClass(item: NavItem, fullWidth = false): string {
    const isMobileOrExpanded = this.mobile() || !this.isDesktopCollapsed();
    const active = this.isActiveItem(item)
      || (this.isDesktopCollapsed() && this.hasActiveChild(item));
    const base = 'flex w-full items-center gap-2 overflow-clip transition-all';
    const sizeClass = !isMobileOrExpanded ? this.collapsedItemClass : this.expandedItemClass;
    const stateClass = active ? this.activeItemClass : this.inactiveItemClass;
    return `${base} ${sizeClass} ${stateClass}`;
  }

  childLinkClass(item: NavItem): string {
    const active = this.isActiveItem(item);
    const base = 'flex h-[52px] w-full items-center gap-2 overflow-clip rounded-lg px-6 text-sm font-normal leading-6 transition-all';
    return `${base} ${active ? this.activeItemClass : this.inactiveItemClass}`;
  }

  logoutClass(): string {
    const isMobileOrExpanded = this.mobile() || !this.isDesktopCollapsed();
    const base = 'flex w-full items-center gap-2 overflow-clip transition-all';
    const sizeClass = isMobileOrExpanded ? this.expandedItemClass : this.collapsedItemClass;
    return `${base} ${sizeClass} ${this.inactiveItemClass}`;
  }

  private flattenNavigation(items: NavItem[]): NavItem[] {
    return items.flatMap(item => [item, ...this.flattenNavigation(item.children ?? [])]);
  }

  private matchMenuItem(url: string, items: NavItem[]): NavItem | null {
    const currentUrl = this.contextualUrl(url);
    const candidates = items
      .map(item => ({ item, specificity: this.routeMatchLength(currentUrl, item.href) }))
      .filter(candidate => candidate.specificity > 0)
      .sort((a, b) => {
        // La ruta (o alias) más larga que coincide gana: así un alias amplio no le quita el sitio a un ítem exacto.
        const routeSpecificity = b.specificity - a.specificity;
        if (routeSpecificity !== 0) return routeSpecificity;

        return Number(this.hasChildren(a.item)) - Number(this.hasChildren(b.item));
      });

    return candidates[0]?.item ?? null;
  }

  private keepPreviousInSection(url: string, previous: NavItem | null, items: NavItem[]): NavItem | null {
    if (!previous || !items.includes(previous)) return null;

    const section = (path: string) => this.normalizeUrl(path).split('/')[1] ?? '';
    const currentSection = section(url);
    return currentSection && currentSection === section(previous.href) ? previous : null;
  }

  private contextualUrl(url: string): string {
    const path = this.normalizeUrl(url);
    if (path !== '/reconciliation/cuadre' && !path.startsWith('/reconciliation/configuracion')) return path;

    const query = url.split('?')[1]?.split('#')[0] ?? '';
    const from = new URLSearchParams(query).get('from');
    return (from && AppSidebarComponent.CONTEXT_ROUTES[from]) || path;
  }

  /** Largo de la ruta (o alias) de `href` que coincide con la URL actual; 0 si no coincide. */
  private routeMatchLength(currentUrl: string, href: string): number {
    if (!href) return 0;

    const normalizedHref = this.normalizeUrl(href);

    if (!normalizedHref) return 0;
    const routes = [normalizedHref, ...(AppSidebarComponent.ROUTE_ALIASES.get(normalizedHref) ?? [])];
    if (routes.includes('/')) return currentUrl === '/' ? 1 : 0;

    return Math.max(0, ...routes
      .filter(route => currentUrl === route || currentUrl.startsWith(`${route}/`))
      .map(route => route.length));
  }

  private normalizeUrl(url: string): string {
    const [path] = url.split(/[?#]/);
    const withoutTrailingSlash = path.length > 1 ? path.replace(/\/+$/, '') : path;
    return withoutTrailingSlash || '/';
  }
}
